/**
 * HL-11800 / HL-11986 — guest-only email capture inside the Variant B questionnaire, always
 * right before the Legal Consent page (the HL-11800 position test closed with that spot).
 * HL-12595: the HL-11986 design test closed with arm C (Figma 7074:23618), now the only design.
 *
 *   title
 *   [Mit Google fortfahren]    → existing Google OAuth; the user comes back signed in and the
 *                                step is skipped like for any signed-in customer
 *   ── ODER MIT E-MAIL FORTFAHREN ──
 *   E-Mail input
 *   [ ] marketing opt-in       → stored as leads.marketing_consent
 *   ── CTA slot ──
 *   [Weiter zur Behandlungsauswahl]  (always enabled: empty field = skip)
 *   Überspringen
 *
 * The lead is a marketing/ops nice-to-have and the questionnaire submit is the conversion, so a
 * failed or rate-limited POST never blocks the funnel — it's logged and the user moves on.
 */

import React, { useEffect, useRef, useState } from "react";
import QuestionFrame from "./QuestionFrame";
import PrimaryCtaButton from "./PrimaryCtaButton";
import { ShellScopedId } from "./shellContext";
import InputField from "../../../components/CredentialsInput/InputField";
import GoogleLoginButton from "../../../components/LoginStrategies/GoogleLoginButton";
import * as http from "../../../utils/http";
import * as translator from "../../../utils/translator";
import { validateEmail } from "../../../utils/fieldsValidator";
import { track as trackGTM } from "../../../PixelTrackers/GtmTracker";
import { MainTracker } from "../../../PixelTrackers/MainTracker";
import { getLastArrivalUtms } from "../../../utils/arrivalUtms";
import {
  markWlEmailStepOAuthReturn,
  clearWlEmailStepOAuthReturn,
  WL_EMAIL_STEP_POSITION,
} from "../../../utils/wlEmailStepExperiment";

const t = (language: string | undefined, key: string): string =>
  translator.translate(language, "default", "QuestionnairePage/" + key) || "";

// Same swallow-the-request timeout as useChooseTreatmentCarePlus: http.postToServer never
// settles on a 401 (it reloads instead) and a hung request must not stall the submit.
const LEAD_POST_TIMEOUT_MS = 8000;

export type ContactDetailsOutcome = "submitted" | "skipped";

interface ContactDetailsPageProps {
  language?: string;
  country: string;
  mention: string;
  categoryID: number | null;
  /** An email was already captured this session — never POST a second lead row. */
  alreadySubmitted?: boolean;
  stepCurrent: number;
  stepTotal: number;
  onBack: () => void;
  /** Forwarded to QuestionFrame (required there); this step renders no sign-in link itself. */
  onSignIn: () => void;
  /** Advances to the next screen. Awaited so the buttons stay disabled meanwhile. */
  onDone: (outcome: ContactDetailsOutcome) => Promise<void>;
}

/**
 * Figma "Ghost" link text: Poppins 14/1.5, gradient-filled and underlined. Green
 * (#15967c → #72b33e) for the sign-in link, orange (#e07409 → #ffa44e) for Skip. Inline styles
 * on a span (not on the button) so the global button reset can't wipe the background-image
 * the clip relies on; the underline gets its own colour because the text colour itself is
 * transparent for the gradient clip.
 */
const LINK_GRADIENTS = {
  green: { image: "linear-gradient(90deg, #15967c 0%, #72b33e 100%)", underline: "#15967c" },
  orange: { image: "linear-gradient(91deg, #e07409 0%, #ffa44e 82%)", underline: "#e07409" },
} as const;

const GradientLinkText: React.FC<{ tone?: keyof typeof LINK_GRADIENTS; children: React.ReactNode }> = ({
  tone = "green",
  children,
}) => (
  <span
    className="font-['Poppins'] text-[14px] leading-[1.5] underline"
    style={{
      backgroundImage: LINK_GRADIENTS[tone].image,
      WebkitBackgroundClip: "text",
      backgroundClip: "text",
      color: "transparent",
      WebkitTextFillColor: "transparent",
      textDecorationColor: LINK_GRADIENTS[tone].underline,
    }}
  >
    {children}
  </span>
);

const CheckIcon: React.FC = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden>
    <path d="M2.5 6.2L4.8 8.5L9.5 3.5" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const LINK_BUTTON_CLASS =
  "font-['Poppins'] text-[14px] leading-[1.5] text-center bg-transparent border-none p-0 cursor-pointer " +
  "hover:opacity-80 disabled:opacity-60 disabled:cursor-not-allowed transition";

const emailDomainOf = (email: string): string | null => {
  const at = email.lastIndexOf("@");
  return at >= 0 ? email.slice(at + 1).toLowerCase() : null;
};

const ContactDetailsPage: React.FC<ContactDetailsPageProps> = ({
  language,
  country,
  mention,
  categoryID,
  alreadySubmitted = false,
  stepCurrent,
  stepTotal,
  onBack,
  onSignIn,
  onDone,
}) => {
  const [email, setEmail] = useState("");
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [emailTouched, setEmailTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  // Ref mirrors `busy` so a double tap in the same tick can't start two flows.
  const busyRef = useRef(false);
  // A retry after a failed questionnaire submit (or Back → Continue again) must not
  // insert a second lead row; seeded from the session-level flag.
  const postedRef = useRef(alreadySubmitted);
  const mountedRef = useRef(true);
  useEffect(() => () => { mountedRef.current = false; }, []);

  const stepProps = { step_position: WL_EMAIL_STEP_POSITION };

  useEffect(() => {
    MainTracker.track("custom", "wl_email_step_viewed", { ...stepProps, country });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const trimmedEmail = email.trim();
  const emailValidation = validateEmail(trimmedEmail, language) as { isValid: boolean; errorMsg: string };
  const showEmailError = emailTouched && trimmedEmail.length > 0 && !emailValidation.isValid;

  // Keep Google OAuth returning to this same questionnaire page (same as SignInModal).
  const currentPath = `${window.location.pathname.replace(/^\//, "")}${window.location.search}`;

  const finish = async (outcome: ContactDetailsOutcome) => {
    // Back was pressed mid-POST: the stack already popped, don't advance from there.
    if (!mountedRef.current) return;
    // A bfcache-restored tab (Google → Back without a reload) still holds the OAuth
    // return marker; the user is moving on, so it must not steer a later reload.
    clearWlEmailStepOAuthReturn();
    await onDone(outcome);
    // Normally unmounted by now (the next screen replaced us); re-enable only if we're still here.
    busyRef.current = false;
    if (mountedRef.current) setBusy(false);
  };

  const handleContinue = async () => {
    if (busyRef.current) return;

    // Product: the CTA never blocks the funnel. An empty field is a skip; only a
    // non-empty, invalid address stops here and shows the inline error.
    if (trimmedEmail.length === 0) {
      await skip("continue_without_email");
      return;
    }
    setEmailTouched(true);
    if (!emailValidation.isValid) return;

    busyRef.current = true;
    setBusy(true);

    if (!postedRef.current) {
      try {
        const request = http.postToServer(
          "leads",
          {
            email: trimmedEmail,
            marketingConsent,
            country,
            language,
            catalogID: categoryID,
            // Campaign params of the last arrival (tracking bootstrap) → leads.utm + HubSpot.
            utm: getLastArrivalUtms(),
            // HubSpot form context; no query string so no PII/UTMs leak into the page URI.
            pageUri: `${window.location.origin}${window.location.pathname}`,
          },
          [http.MIDDLEWARES.CUSTOMER_ID],
        );
        const timeout = new Promise<never>((_, reject) =>
          setTimeout(() => reject(new Error("lead post timeout")), LEAD_POST_TIMEOUT_MS),
        );
        await Promise.race([request, timeout]);
      } catch {
        // Never block the funnel on the lead — the server logs/counters cover the failure.
      }
      // Whatever the POST did, don't nag the user again this session.
      postedRef.current = true;
      MainTracker.track("custom", "wl_email_step_submitted", {
        ...stepProps,
        email_domain: emailDomainOf(trimmedEmail),
        marketing_consent: marketingConsent,
      });
      trackGTM("custom", "ga4_auto_event", {
        event_name: "lead_contact_submit",
        subcategory: mention,
        step_position: WL_EMAIL_STEP_POSITION,
      });
    }

    await finish("submitted");
  };

  // `method` tells analytics whether the user tapped Skip or hit Continue with an empty field.
  const skip = async (method: "skip_link" | "continue_without_email") => {
    if (busyRef.current) return;
    busyRef.current = true;
    setBusy(true);
    MainTracker.track("custom", "wl_email_step_skipped", { ...stepProps, method });
    trackGTM("custom", "ga4_auto_event", {
      event_name: "lead_contact_skip",
      subcategory: mention,
      step_position: WL_EMAIL_STEP_POSITION,
      method,
    });
    await finish("skipped");
  };

  const handleSkip = () => skip("skip_link");

  return (
    <QuestionFrame
      showHero={false}
      pageId="wl2_contact_page"
      // No sign-in prompt on this step (Google button above covers the signed-in path).
      showSignInLink={false}
      stepCurrent={stepCurrent}
      stepTotal={stepTotal}
      // Poppins Medium 24/1.3 black, 32px to the first block, no subtitle.
      titleHtml={`<div class="text-center">${t(language, "contact_title")}</div>`}
      titleClassName="font-['Poppins'] font-medium text-black text-[24px] leading-[1.3] mb-8 [word-break:break-word]"
      onBack={onBack}
      onSignIn={onSignIn}
      language={language}
      cta={
        // Wrapped in one div so QuestionFrame's mobile CTA clone gets a single element and the
        // gap survives its `[&_button]:!mt-0` reset. Figma: 54px CTA (12px radius, Poppins
        // Medium 16), then the Skip link (Poppins 14/1.5) 16px below.
        <div className="flex flex-col items-center w-full gap-4">
          <PrimaryCtaButton
            id="wl2_contact_continue"
            label={t(language, "contact_continue")}
            onClick={handleContinue}
            // Always enabled (ticket AC-14): an empty field continues as a skip, an invalid
            // address shows the inline error instead of advancing.
            disabled={busy}
            className="!rounded-[12px] !font-medium"
          />
          <div className="flex items-center justify-center">
            {/* Rides along in the `cta` element, so it clones into the mobile bar too. */}
            <ShellScopedId base="wl2_contact_skip">
              {(skipId) => (
                <button id={skipId} type="button" onClick={handleSkip} disabled={busy} className={LINK_BUTTON_CLASS}>
                  <GradientLinkText tone="orange">{t(language, "contact_skip")}</GradientLinkText>
                </button>
              )}
            </ShellScopedId>
          </div>
        </div>
      }
    >
      <ShellScopedId base="wl2_contact_form">
        {(formId) => (
      <form
        id={formId}
        // Google → divider → email as one 24px column, 24px to the opt-in row.
        className="flex flex-col w-full gap-6"
        onSubmit={(e) => { e.preventDefault(); void handleContinue(); }}
        noValidate
      >
        <div className="flex flex-col items-center w-full gap-6">
          {/* Figma MainButton neutral-medium: 64px, #f5f6f7 on a 1px white border, 12px radius,
              soft up/down shadow, 24px Google mark, Poppins Medium 18 #08080a. */}
          <ShellScopedId base="wl2_contact_google_button">
            {(googleId) => (
          <GoogleLoginButton
            id={googleId}
            redirectPath={currentPath}
            cameFromPath={currentPath}
            language={language || ""}
            country={country}
            // The redirect drops the screen stack; QuestionnaireVariantB reads this marker on
            // the way back to land on this step again (guest) or on its continuation (signed in).
            onClick={() => markWlEmailStepOAuthReturn()}
            label={t(language, "contact_google_button")}
            className="flex items-center justify-center gap-2 w-full h-[64px] px-8 bg-[#f5f6f7] border border-solid border-white rounded-[12px] shadow-[0_-12px_16px_rgba(255,255,255,0.82),0_12px_18px_rgba(206,208,216,0.7)] cursor-pointer hover:opacity-80 transition-opacity duration-300 outline-none"
            iconClassName="size-6 shrink-0"
            labelClassName="font-['Poppins'] font-medium text-[18px] leading-none text-[#08080a] m-0"
          />
            )}
          </ShellScopedId>

          {/* Figma Divider: 1px #E5E9F0 lines, 5px, Poppins 12/24 #adadad uppercase (same
              pattern as SignInModal). */}
          <ShellScopedId base="wl2_contact_divider">
            {(dividerId) => (
              <div id={dividerId} className="flex items-center gap-[5px] w-full">
                <div className="h-px flex-1 bg-[#E5E9F0]" aria-hidden />
                <span className="font-['Poppins'] text-[12px] leading-[24px] tracking-[-0.132px] uppercase text-[#adadad] whitespace-nowrap">
                  {t(language, "contact_or_divider")}
                </span>
                <div className="h-px flex-1 bg-[#E5E9F0]" aria-hidden />
              </div>
            )}
          </ShellScopedId>

          {/* Figma MainInput: "E-Mail" label (14 #616975, no asterisk), 8px, 60px field with a
              12px radius and #dee3ed border, "name@beispiel.de" placeholder in #99a3b2. */}
          {/* `id` overrides InputField's internal useId(), which is what `label htmlFor`
              binds to. QuestionFrame renders this whole subtree in both shells, so an
              unsuffixed id would give both labels the same target and the browser would
              pair them with the first (hidden, desktop) input. */}
          <ShellScopedId base="wl2_contact_email">
            {(emailInputId) => (
              <InputField
                id={emailInputId}
                value={email}
                errorMsg={showEmailError ? emailValidation.errorMsg : ""}
                onChange={(value: string) => setEmail(value)}
                type="email"
                label={t(language, "contact_email_label")}
                placeholder={t(language, "contact_email_placeholder")}
                language={language}
                mandatory={false}
                inputClassName="!h-[60px]"
                onBlur={() => setEmailTouched(true)}
                touched={emailTouched}
              />
            )}
          </ShellScopedId>
        </div>
        <input type="submit" hidden />

        {/* Marketing opt-in — Figma switchers/checkbox: 24px hit area with a 20px box, 1px
            #b5bdcc border, 2px radius; 24px to the copy (Poppins 12/18 #616975) whose
            "Datenschutzerklärung" is a #00734a underlined link opening in a new tab. Optional.
            The copy is a sibling of the checkbox button, not a child, so the privacy link stays
            a real link (no <a> inside <button>); clicking the plain text still toggles the box. */}
        <div className="flex items-center w-full gap-6">
          <ShellScopedId base="wl2_contact_marketing_consent">
            {(consentId) => (
          <button
            id={consentId}
            type="button"
            role="checkbox"
            aria-checked={marketingConsent}
            aria-label={t(language, "contact_marketing_consent").replace(/<[^>]+>/g, "")}
            onClick={() => setMarketingConsent((prev) => !prev)}
            className="shrink-0 size-6 flex items-center justify-center bg-transparent border-none p-0 cursor-pointer"
          >
            <span
              className="size-5 rounded-[2px] flex items-center justify-center box-border"
              style={
                marketingConsent
                  ? { backgroundColor: "#f69503", border: "none" }
                  : { backgroundColor: "transparent", border: "1px solid #b5bdcc" }
              }
              aria-hidden
            >
              {marketingConsent ? <CheckIcon /> : null}
            </span>
          </button>
            )}
          </ShellScopedId>
          <ShellScopedId base="wl2_contact_marketing_consent_text">
            {(consentTextId) => (
          <span
            id={consentTextId}
            className="font-['Poppins'] text-[12px] leading-[18px] text-[#616975] [word-break:break-word] cursor-pointer [&_a]:text-[#00734a] [&_a]:underline [&_a]:[text-underline-position:from-font]"
            onClick={(e) => {
              if ((e.target as HTMLElement).closest("a")) return; // let the privacy link open
              setMarketingConsent((prev) => !prev);
            }}
            dangerouslySetInnerHTML={{ __html: t(language, "contact_marketing_consent") }}
          />
            )}
          </ShellScopedId>
        </div>
      </form>
        )}
      </ShellScopedId>
    </QuestionFrame>
  );
};

export default ContactDetailsPage;
